/**
 * @file 用户信息模块
 * @author Justineo(justice360@gmail.com) Chestnut
 */

import {extend, each, clone, isString} from 'lodash';
import qs from 'qs';
import Auth from './Auth';
import {purify} from '../utils/lang';

// 'VISITOR_ONLY' 'ADER_ONLY' 'CEILING' 'FLOOR'
function getAuthMapByCompare(user, type) {
    let visitor = user.getVisitor();
    let ader = user.getAder();
    if (!ader) {
        return visitor.auth;
    }

    let authMap = clone(visitor.auth);
    each(ader.auth, (value, field) => {
        if (!authMap[field]) {
            authMap[field] = value;
            return;
        }

        let priorityMap = {
            none: 1,
            readonly: 2,
            editable: 3
        };
        if (type === 'CEILING'
            ? (priorityMap[value] > priorityMap[authMap[field]])
            : (priorityMap[value] < priorityMap[authMap[field]])
        ) {
            authMap[field] = value;
        }
    });
    return authMap;
}
const DEFAULT_AUTH_MERGE_STRATEGIES = {
    VISITOR_ONLY(user) {
        return user.getVisitor().auth;
    },
    ADER_ONLY(user) {
        let ader = user.getAder();
        return ader ? ader.auth : user.getVisitor().auth;
    },
    CEILING(user) {
        return getAuthMapByCompare(user, 'CEILING');
    },
    FLOOR(user) {
        return getAuthMapByCompare(user, 'FLOOR');
    }
};

const DEFAULT_OPTIONS = {
    selfKey: 'visitor',
    userKey: 'adOwner',
    idKey: 'aderId',
    authMergeStrategy: 'ADER_ONLY'
};

export default class User {

    /**
     * @public
     */
    auth = new Auth();

    /**
     * 构造函数
     *
     * @public
     */
    constructor() {
        this.options = DEFAULT_OPTIONS;

        this.authMap = null;
    }

    /**
     * 合并配置
     *
     * @public
     * @param {Object} options 配置参数
     * @param {string} options.selfKey 当前登录用户信息对应的键名，默认是 visitor。用于从 session 接口响应解析当前登录用户信息。
     * @param {string} options.userKey 广告主信息对应的键名，默认是 adOwner。用于从 session 接口响应解析当前广告主信息。
     * @param {string} options.idKey 广告主 id 对应的键名，用于从 url query 部分解析广告主 id 以及传递给后端接口参数。
     * @param {string|Function} options.authMergeStrategy ader 和 visitor 的权限合并策略，内置支持： VISITOR_ONLY 、 ADER_ONLY 、 CEILING 、 FLOOR
     */
    mergeOptions(options) {
        extend(this.options, options);

        if (isString(this.options.authMergeStrategy)) {
            this.options.authMergeStrategy = DEFAULT_AUTH_MERGE_STRATEGIES[this.options.authMergeStrategy];
        }
    }

    /**
     * 初始化，只能调用一次。
     *
     * @param {Object} session 从 session 接口拿到的数据
     */
    init(session) {
        let options = this.options;
        if (session[options.selfKey]) {
            this.visitor = session[options.selfKey];
        }
        if (session[options.userKey]) {
            this.ader = session[options.userKey];
        }
        if (!session[options.selfKey] && !session[options.userKey]) {
            this.visitor = session;
        }

        // 初始化权限
        this.authMap = this.options.authMergeStrategy(this);
    }

    /**
     * 获取 visitor 信息
     *
     * @public
     * @return {Object}
     */
    getVisitor() {
        return this.visitor || null;
    }

    /**
     * 获取 visitor id
     *
     * @public
     * @return {string}
     */
    getVisitorId() {
        return this.visitor && this.visitor.id;
    }

    /**
     * 获取 ader 信息
     *
     * @public
     * @return {Object}
     */
    getAder() {
        return this.ader || null;
    }

    /**
     * 获取 ader id
     *
     * @public
     * @return {string}
     */
    getAderId() {
        let idKey = this.options.idKey;
        return (this.ader && this.ader.id)
            || qs.parse(document.location.search.slice(1))[idKey];
    }

    /**
     * 构造传递给后端接口的参数。一般来说，如果是代理商代理广告主登录，那么每次请求后端接口的时候都需要带上被代理广告主的 id 。
     *
     * @public
     * @return {Object}
     */
    getAderArgMap() {
        let id = this.getAderId();
        let args = {
            [this.options.idKey]: id
        };
        return purify(args);
    }

    /**
     * 获取权限对象。
     *
     * @public
     * @return {Object}
     */
    getAuthMap() {
        return this.authMap;
    }

    /**
     * 获取权限类型
     *
     * @public
     * @param {string} authId auth id
     */
    getAuthType(authId) {
        return this.auth.get(authId, this.getAuthMap());
    }

    /**
     * 获取某个权限更加友好的描述对象
     *
     * @public
     * @param {string} authId auth id
     * @return {Object}
     */
    getAuth(authId) {
        let authType = this.getAuthType(authId);
        return {
            type: authType,
            id: authId,
            isReadOnly: authType === Auth.TYPE.READONLY,
            isEditable: authType === Auth.TYPE.EDITABLE,
            isVisible: authType !== Auth.TYPE.NONE,
            isNone: authType === Auth.TYPE.NONE
        };
    }
}
